import {
  AfterViewInit, Component, ElementRef, ErrorHandler, Inject, NgZone, OnInit, Renderer2,
  ViewChild
} from '@angular/core';
import {
  DefaultUrlSerializer, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, PRIMARY_OUTLET, Router,
  UrlSerializer, UrlTree
} from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { filter } from 'rxjs/operators';
import * as Raven from 'raven-js';

import { AuthService } from './auth/auth/auth.service';
import { UserService } from './shared/user/user.service';
import { GoogleService } from './google/google.service';
import { HistoryTrackerService } from './shared/history-tracker/history-tracker.service';
import { UserInfo } from './shared/models/user-info.model';
import { SkinService } from './shared/skin/skin.service';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit {
  @ViewChild('loaderEl') loaderEl: ElementRef;
  @ViewChild('googleAttributionEl') googleAttributionEl: ElementRef;

  private _serializer: UrlSerializer;
  showHeader = true;
  showFooter = true;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private router: Router,
    private ngZone: NgZone,
    private renderer: Renderer2,
    private _skin: SkinService,
    private _google: GoogleService,
    private _auth: AuthService,
    private _userService: UserService,
    private _history: HistoryTrackerService,
    private _errorHandler: ErrorHandler
  ) {
    this._serializer = new DefaultUrlSerializer();

    // Show spinner when navigating between routes
    this.router.events.subscribe(event => {
      this._navigationInterceptor(event);
    });
  }

  async ngOnInit() {
    // Subscribe to user info events
    this._userService.userInfo.subscribe(userInfo => this._onUserInfoUpdated(userInfo));

    // Authenticate on startup
    try {
      await this._auth.login();
    } catch (e) {
      this._errorHandler.handleError(e);
    }

    // this._initUserEcho();

    // Scroll back to top after navigation events
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.renderer.setProperty(this.document.documentElement, 'scrollTop', 0);
      this.renderer.setProperty(this.document.body, 'scrollTop', 0);
    });
  }

  async ngAfterViewInit() {
    await Promise.all([
      this._skin.load(),
      this._google.init(this.googleAttributionEl.nativeElement)
    ]);
  }

  private _onUserInfoUpdated(userInfo: UserInfo) {
    if (userInfo) {
      // Update user info for error logging
      Raven.setUserContext({
        id: userInfo.masterCustomerId,
        username: `${userInfo.firstName} ${userInfo.lastName}`
      });
    } else {
      // Clear user info in log events
      Raven.setUserContext();
    }
  }

  // Shows and hides the loading spinner during route changes
  private _navigationInterceptor(event): void {
    if (event instanceof NavigationStart) {
      // We want to run this function outside of Angular's zone to bypass change detection
      this.ngZone.runOutsideAngular(() => {
        this.renderer.setStyle(
          this.loaderEl.nativeElement,
          'display',
          'block'
        );
      });
    }
    if (event instanceof NavigationEnd) {
      this.hideSpinner();
      this._onNavigatePage(event.url);
    }
    // Set loading state to false in both of the below events to hide the spinner in case a request fails
    if (event instanceof NavigationCancel) {
      this.hideSpinner();
    }
    if (event instanceof NavigationError) {
      this.hideSpinner();
    }
  }

  /**
   * Method to run when we navigate to a different page within the app. This sets flags/overrides
   * as appropriate for the current page.
   * @param url {string} Current page URL
   * @private
   */
  private _onNavigatePage(url) {
    // @ts-ignore
    const $body = $('body');
    const tree = this.router.parseUrl(url);

    // Update header/footer visibility based on current route
    this.showHeader = this.shouldShowHeader(tree);
    this.showFooter = this.shouldShowFooter(tree);

    // Add or remove the full-page CSS class to the body tag, depending on whether we are on the search page.
    // The search page is designed to be a full page experience with no vertical scroll bar. However, some of
    // the analytics code adds invisible elements to the body that cause it to overflow and adds a scrollbar.
    // Adding the full-page CSS class is a workaround that sets overflow: hidden on the body, removing the
    // scroll bar.
    $body.removeClass('full-page');
    const group = tree.root.children[PRIMARY_OUTLET];
    if (group) {
      const segment = group.segments[0];
      if (segment) {
        if (segment.path === 'search') {
          $body.addClass('full-page');
        }
      }
    }
  }

  /**
   * Returns true if the main site header should be displayed based on the provided route.
   * @param tree {UrlTree} Current route in the application
   * @return {boolean} True if the main site header should be displayed
   */
  shouldShowHeader(tree: UrlTree): boolean {
    const group = tree.root.children[PRIMARY_OUTLET];
    if (group) {
      const segment = group.segments[0];
      if (segment) {
        switch (segment.path) {
          case 'search':
            // Suppress header on the search page
            return false;
          /* case 'airports':
            // Show header on the "Airports by State" directory, but not on any of the airport detail pages.
            const segment2 = group.segments[1];
            return !!(segment2 && segment2.path === 'state'); */
        }
      }
    }
    return true;    // Show header by default
  }

  /**
   * Returns true if the main site footer should be displayed based on the provided route.
   * @param tree {UrlTree} Current route in the application
   * @return {boolean} True if the main site footer should be displayed
   */
  shouldShowFooter(tree: UrlTree): boolean {
    const group = tree.root.children[PRIMARY_OUTLET];
    if (group) {
      const segment = group.segments[0];
      if (segment) {
        switch (segment.path) {
          case 'search':
            // Suppress footer on the search page
            return false;
        }
      }
    }
    return true;    // Show footer by default
  }

  hideSpinner(): void {
    this.ngZone.runOutsideAngular(() => {
      this.renderer.setStyle(
        this.loaderEl.nativeElement,
        'display',
        'none'
      );
    });
  }

  private async _initUserEcho() {
    const ues = {
      host: 'aopa.userecho.com',
      forum: '7',
      lang: 'en',
      no_dialog: true,
      tab_corner_radius: 5,
      tab_font_size: 20,
      tab_image_hash: 'ZmVlZGJhY2s%3D',
      tab_chat_hash: 'Y2hhdA%3D%3D',
      tab_alignment: 'right',
      tab_text_color: '#ffffff',
      tab_text_shadow_color: '#00000055',
      tab_bg_color: '#00a3e0',
      tab_hover_color: '#f45c5c',
      params: {}
    };
    if (this._auth.authenticated && this._auth.endUser) {
      const SSOTokenPromise = this._userService.getUserechoInfo();
      const SSOToken = await SSOTokenPromise.then(result => result);
      ues.params = { sso_token: SSOToken };
    }
    window['_ues'] = ues;
    (function() {
      const _ue = document.createElement('script'); _ue.type = 'text/javascript'; _ue.async = true;
      _ue.src = ('https:' === document.location.protocol ? 'https://' : 'http://') + 'cdn.userecho.com/js/widget-1.4.gz.js';
      const s = document.getElementsByTagName('script')[0]; s.parentNode.insertBefore(_ue, s);
    })();
  }
}
